/**
 * SlidersBuilder — auto-balanced weighted sub-metrics
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import type { FormulaSlidersBody, FormulaSubMetric } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { pickMetric, useAllowLowest, withLowest } from '@/features/flow-optimizer/tabs/calculation/sliders/lowest';

import SubMetricSliderRow from './SubMetricSliderRow';
import { appendMetricBalanced, removeMetricBalanced } from './sliders.balance';
import { useAutoBalancedSliders } from '../../../hooks';
import { rebalance } from '../../../hooks/auto-balanced-sliders.math';

export interface SlidersBuilderProps {
  body: FormulaSlidersBody;
  onChange: (next: FormulaSlidersBody) => void;
}

const SlidersBuilder: FC<SlidersBuilderProps> = ({ body, onChange }) => {
  const initialWeights = useMemo(() => buildWeights(body.metrics), [body.metrics]);
  const sliders = useAutoBalancedSliders(initialWeights);

  const allowLowest = useAllowLowest();
  const update = (next: FormulaSubMetric[]) => onChange({ metrics: next });
  // Also writes the displayed (balanced) weights through so legacy
  // zero-weight bodies heal on the first metric pick (or Highest/Lowest flip).
  const setRow = (id: string, edit: (m: FormulaSubMetric) => FormulaSubMetric) =>
    update(body.metrics.map((m) => ({ ...(m.id === id ? edit(m) : m), weight: sliders.values[m.id] ?? m.weight })));
  const setMetric = (id: string, metric: string) => setRow(id, (m) => pickMetric(m, metric, allowLowest));
  const setLowest = (id: string, lowest: boolean) => setRow(id, (m) => withLowest(m, lowest));
  const setWeight = (id: string, next: number) => {
    // Rebalance using the *currently displayed* slider values (not the stored
    // body weights — those can lag at 0 when the user has only picked a
    // metric without dragging yet). Then sync both the auto-balance hook and
    // the persisted body in one shot so the saved expression always matches
    // what the sliders show on screen.
    const balanced = rebalance(sliders.values, { id, target: next });
    sliders.reset(balanced);
    update(
      body.metrics.map((m) => ({
        ...m,
        weight: balanced[m.id] ?? m.weight,
      })),
    );
  };
  // Add/remove write the balanced weights into the body so the serialized
  // draft always matches the UI (FRM-sliders-weight-desync regression).
  const remove = (id: string) => update(removeMetricBalanced(body.metrics, id));
  const add = () => update(appendMetricBalanced(body.metrics));

  if (body.metrics.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border px-4 py-5 text-center">
        <p className="text-xs text-muted-foreground">
          No sub-metrics yet. Add weighted metrics; their weights inside the group sum to 100%.
        </p>
        <Button variant="outline" size="sm" type="button" onClick={add} className="mt-2">
          + Add metric
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {body.metrics.map((m) => (
        <SubMetricSliderRow
          key={m.id}
          metric={{ ...m, weight: sliders.values[m.id] ?? m.weight }}
          onMetricChange={(next) => setMetric(m.id, next)}
          onWeightChange={(next) => setWeight(m.id, next)}
          onRemove={() => remove(m.id)}
          onLowestChange={(lowest) => setLowest(m.id, lowest)}
        />
      ))}
      <Button variant="outline" size="sm" type="button" onClick={add}>
        + Add metric
      </Button>
    </div>
  );
};

function buildWeights(metrics: FormulaSubMetric[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of metrics) out[m.id] = m.weight;
  return out;
}

export default SlidersBuilder;
